import { FunctionComponent, useEffect, useState } from "react"
import { Alert, Button, Col, Row } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate, useParams } from "react-router-dom"
import KeycardDoorsTable from "../components/KeycardDoorsTable"
import TenantForm, { TenantErrors, TenantTouched } from "../components/TenantForm"
import { Tenant } from "../data/models.interface"
import { useGetDoorsQuery } from "../hooks/queries/useGetDoorsQuery"
import { useGetKeycardDoorsQuery } from "../hooks/queries/useGetKeycardDoorsQuery"
import { useGetTenantQuery } from "../hooks/queries/useGetTenantQuery"
import { useUpdateTenantQuery } from "../hooks/queries/useUpdateTenantQuery"
import { Parameters } from "../hooks/useLazyQuery"

interface ITenantPage {}
const TenantPage: FunctionComponent<ITenantPage> = () => {
	const params = useParams()
	const tenantId = params.tenantId!
	//
	const tenantQuery = useGetTenantQuery(tenantId)
	const updateTenantQuery = useUpdateTenantQuery(tenantId)
	const keycardDoorsQuery = useGetKeycardDoorsQuery("tenant")
	//
	const doorsQuery = useGetDoorsQuery()
	const doors = doorsQuery.data?.doors
	//
	const [tenant, setTenant] = useState<Tenant>()
	const [touched, setTouched] = useState<TenantTouched>({})
	const validationErrors = validate(tenant)
	const modified = isModified(tenant, tenantQuery.data)
	//
	useEffect(() => {
		// only init state once
		if (tenant == null) {
			if (tenantQuery.data) {
				setTenant(JSON.parse(JSON.stringify(tenantQuery.data)))
				keycardDoorsQuery.setEntityFilter(tenantQuery.data.Name)
			}
		}
	}, [tenantId, tenantQuery.data, tenant, keycardDoorsQuery])
	// loading layout
	const loading = !tenant
	const error = updateTenantQuery.error || tenantQuery.error
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container>
			<h2 className="text-center mt-4">Resident</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<Alert variant="info">
				<ul>
					<li>
						If the resident units listed here are not accurate, double check that they are accurate in Rent Manager;
						then press Refresh.
					</li>
					<li>If you need to ban a resident from a specific door, add it under Forbidden Doors; then press Save.</li>
					<li>If you need to add or remove a keycard, do that here; then press Save.</li>
					<li>
						If all of that is correct, you can check the list of permissions at the bottom to see their access for
						specific doors. If the permissions at the bottom are incorrect, press Refresh and then check again.
					</li>
					<li>
						Once changes appear in the list of permissions, they should usually be applied to the card readers within
						10-15 minutes.
					</li>
				</ul>
			</Alert>
			<TenantForm
				tenant={tenant}
				setTenant={setTenant}
				doors={doors}
				touched={touched}
				setTouched={setTouched}
				validationErrors={validationErrors}
			/>
			<Row>
				<Col xs="auto">
					<BackButton loading={updateTenantQuery.loading || updateTenantQuery.data != null} />
				</Col>
				<Col></Col>
				<Col xs="auto">
					<RefreshButton
						modified={modified}
						query={updateTenantQuery.query}
						loading={updateTenantQuery.loading || updateTenantQuery.data != null}
					/>
				</Col>
				<Col xs="auto">
					<SaveButton
						tenant={tenant}
						modified={modified}
						setTouched={setTouched}
						validationErrors={validationErrors}
						query={updateTenantQuery.query}
						loading={updateTenantQuery.loading || updateTenantQuery.data != null}
					/>
				</Col>
			</Row>
			{keycardDoorsQuery.entityFilter?.length > 0 && (
				<Row>
					<h4 className="text-center">Permissions</h4>
					<KeycardDoorsTable keycardDoorsQuery={keycardDoorsQuery} hideColumns={{ entity: true }} />
				</Row>
			)}
		</Container>
	)
}
export default TenantPage

interface ILoadingLayout {
	error?: string
}
const LoadingLayout: FunctionComponent<ILoadingLayout> = ({ error }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Resident</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

const BackButton = ({ loading }: { loading: boolean }) => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-4" disabled={loading} onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}
interface IRefreshButton {
	modified: boolean | null
	query: (tenant: Parameters<Partial<Tenant>>) => Promise<void>
	loading: boolean
}
const RefreshButton = ({ modified, query, loading }: IRefreshButton) => {
	return (
		<Button
			variant="primary"
			className="mb-4"
			disabled={loading || modified !== false}
			onClick={async () => {
				//call the update function, but don't actually update any fields
				await query({ body: {} })
				//stay on same page, but refresh
				window.location.reload()
				// navigate(-1)
			}}>
			{loading && modified === false ? "Refreshing..." : "Refresh"}
		</Button>
	)
}

interface ISaveButton {
	tenant: Tenant
	modified: boolean | null
	setTouched: (value: TenantTouched) => void
	validationErrors: TenantErrors
	query: (tenant: Parameters<Partial<Tenant>>) => Promise<void>
	loading: boolean
}
const SaveButton = ({ tenant, modified, setTouched, validationErrors, query, loading }: ISaveButton) => {
	// const navigate = useNavigate()
	const save = async () => {
		// show errors on all fields
		setTouched({
			keycard1: true,
			keycard2: true,
			keycard3: true,
			keycard4: true,
			keycard5: true,
			keycard6: true,
			keycard7: true,
			keycard8: true,
			keycard9: true,
			keycard10: true,
		})
		//
		try {
			if (Object.values(validationErrors).every((e) => !e)) {
				await query({
					body: {
						UserDefinedValues: tenant.UserDefinedValues.filter(
							(u) => u.Name.startsWith("Key Card ") || u.Name === "Forbidden Doors",
						),
					},
				})
				//stay on same page, but refresh
				window.location.reload()
				// navigate(-1)
			}
		} catch (e) {
			return
		}
	}
	return (
		<Button variant="primary" className="mb-4" disabled={loading || modified === false} onClick={save}>
			{loading && modified !== false ? "Saving..." : "Save"}
		</Button>
	)
}
const validate = (tenant?: Tenant) => {
	const validationErrors: TenantErrors = {}
	return validationErrors
}

const isModified = (a: Tenant | undefined, b: Tenant | undefined) => {
	if (!a || !b) return null
	//
	for (const keycard of [
		"Key Card 1",
		"Key Card 2",
		"Key Card 3",
		"Key Card 4",
		"Key Card 5",
		"Key Card 6",
		"Key Card 7",
		"Key Card 8",
		"Key Card 9",
		"Key Card 10",
		"Forbidden Doors",
	]) {
		const aCard = a.UserDefinedValues.find((v) => v.Name === keycard)?.Value || ""
		const bCard = b.UserDefinedValues.find((v) => v.Name === keycard)?.Value || ""
		if (aCard !== bCard) return true
	}
	return false
}
